import { useState } from 'react';
import type { MailboxProvider } from '@immo/api-contract';
import { Button, Modal } from '@immo/ui-kit';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import {
  offeredProviders,
  redirectToProvider,
  type ImapReconnect,
  stashPendingMailboxConnect,
} from '@/pages/settings/mailbox-connect-flow';
import { ImapConnectStep } from '@/pages/settings/components/imap-connect-step';
import { ProviderChoice } from '@/pages/settings/components/provider-choice';
import { connectPersonalImap } from '@/pages/settings/connect-personal-imap';
import { FormBanner } from '@/components/form';

// The provider-choice dialog (IMM-264 anatomy): the offered set comes from the
// connection's `availableProviders` via `offeredProviders`. Confirming
// mints the provider `authUrl` via IMM-86's connect route, stashes the pending
// connect for the bare `/settings/mailbox/callback` page, and hands the browser
// to the provider — exactly the settings-page flow, reused here.
export type MailboxConnectDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly workspaceId: string;
  // The `/w/:slug` segment — stashed so the callback page can route back.
  readonly workspaceSlug: string;
  // What a NEW connection may choose here; a connected member passes only their own provider.
  readonly availableProviders: ReadonlyArray<MailboxProvider>;
  // Set when the dialog is opened from the calendar: IMAP is dropped, and the
  // callback returns there.
  readonly returnTo?: 'calendar';
  // Called after an IMAP connection was stored and the dialog closed, so the caller reloads its connection.
  readonly onConnected: () => void;
  // An expired IMAP connection: the dialog opens at the IMAP step, seeded from it, and never offers the choice.
  readonly reconnect?: ImapReconnect | undefined;
};

export function MailboxConnectDialog({
  open,
  onClose,
  workspaceId,
  workspaceSlug,
  availableProviders,
  returnTo,
  onConnected,
  reconnect,
}: MailboxConnectDialogProps) {
  const t = useTranslations('pages.workspace.mailboxConnectDialog');
  const offered = offeredProviders(availableProviders, returnTo === 'calendar' ? 'calendar' : 'mailbox');
  const [selected, setSelected] = useState<MailboxProvider>(offered[0] ?? 'GOOGLE');
  const [step, setStep] = useState<'choose' | 'imap'>('choose');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setConnecting(true);
    setError(null);

    try {
      const result = await apiClient.mailbox.connect({ pathParams: { workspaceId }, body: { provider: selected } });

      if (!result.ok) {
        setError(t('error'));
        setConnecting(false);

        return;
      }

      stashPendingMailboxConnect({
        workspaceId,
        workspaceSlug,
        provider: selected,
        ...(returnTo === undefined ? {} : { returnTo }),
      });
      redirectToProvider(result.body.authUrl);
    } catch {
      setError(t('error'));
      setConnecting(false);
    }
  };

  const close = () => {
    setStep('choose');
    onClose();
  };

  if (open && (reconnect !== undefined || step === 'imap')) {
    return (
      <ImapConnectStep
        workspaceId={workspaceId}
        initial={reconnect}
        target={{ purpose: 'personal', connect: (values) => connectPersonalImap(workspaceId, values) }}
        onBack={reconnect !== undefined ? close : () => setStep('choose')}
        onClose={close}
        onConnected={() => {
          close();
          onConnected();
        }}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={connecting}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => (selected === 'IMAP' ? setStep('imap') : void start())}
            disabled={connecting}
          >
            {selected === 'IMAP' ? t('continue-imap') : t('continue')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-base-sm text-text-secondary">{t('intro')}</p>
        <ProviderChoice offered={offered} selected={selected} onSelect={setSelected} label={t('intro')} />
        <p className="text-xs text-text-tertiary">{selected === 'IMAP' ? t('hint-imap') : t('hint-oauth')}</p>
        {/* Names the providers that are missing rather than leaving their
            absence to be guessed at (IMM-530.2, AC 35). */}
        {!availableProviders.includes('IMAP') && (
          <p className="text-xs text-text-tertiary">
            {t(offered.includes('MICROSOFT') ? 'unsupported-providers-with-microsoft' : 'unsupported-providers')}
          </p>
        )}
        {returnTo === 'calendar' && availableProviders.includes('IMAP') && (
          <p className="text-xs text-text-tertiary">{t('calendar-no-imap')}</p>
        )}
        <FormBanner error={error} size="base-sm" />
      </div>
    </Modal>
  );
}
